import { useQuery } from '@tanstack/react-query';
import { createContext, useContext, useMemo, type ReactNode } from 'react';
import { api } from '@/lib/api/client';
import { useStoredState } from '@/lib/hooks';

export interface Center {
  id: string;
  name: string;
  code?: string;
  addressLine1?: string;
  city?: string;
  state?: string;
  pincode?: string;
  totalAreaSqM?: number;
  rules?: string[];
  amenities?: string[];
}

export const ALL_CENTERS = 'all';

interface CenterContextValue {
  centers: readonly Center[];
  isLoading: boolean;
  error: unknown;
  refetch: () => void;
  /** 'all' or a center id. */
  selectedId: string;
  /** The selected center, or null when "All centers" is chosen. */
  selected: Center | null;
  /** Pass straight into API queries: undefined means "every center". */
  centerId: string | undefined;
  setSelectedId: (id: string) => void;
}

const CenterContext = createContext<CenterContextValue | null>(null);

export const centersQueryKey = ['centers'] as const;

/** Loads the owner's centers once and remembers which one is selected across the app. */
export function CenterProvider({ children }: { children: ReactNode }) {
  const query = useQuery({ queryKey: centersQueryKey, queryFn: ({ signal }) => api.get<Center[]>('/centers', { signal }) });
  const [storedId, setSelectedId] = useStoredState<string>('tcm.center', ALL_CENTERS);

  const value = useMemo<CenterContextValue>(() => {
    const centers = query.data ?? [];
    // A stored id that no longer exists falls back to "all" instead of showing nothing.
    const selected = centers.find((center) => center.id === storedId) ?? null;
    return {
      centers,
      isLoading: query.isPending,
      error: query.error,
      refetch: () => void query.refetch(),
      selectedId: selected?.id ?? ALL_CENTERS,
      selected,
      centerId: selected?.id,
      setSelectedId,
    };
  }, [query, storedId, setSelectedId]);

  return <CenterContext.Provider value={value}>{children}</CenterContext.Provider>;
}

export function useCenter(): CenterContextValue {
  const context = useContext(CenterContext);
  if (!context) {
    throw new Error('useCenter must be used inside the owner layout');
  }
  return context;
}
